import React, { useRef, useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import styled, { css, keyframes } from 'styled-components';
import { gsap } from 'gsap';

import Env from 'lib/Env';
import { getUsageDevButtonsEnabled } from 'lib/browserStorage';
import { getClampedPercentage } from 'lib/utilityFunctions';
import { getSelectedProductUsage, getWeeklyUsageTarget } from 'a/selectors/usageSelectors';
import { getHasUserInteracted } from 'a/selectors/appSessionSelectors';
import { saveUsageData } from 'a/hub/actions/questsActions';
import { ConfettiBurst, DevButtons } from 'a/hub/components/WeeklyUsage';

import { parseMarkdownBold } from '../../utils/helpers';
import useWorkshopSfx from '../../hooks/useWorkshopSfx';
import ConfettiHornSvg from './svgs/workshop-confetti.svg';
import Olivia, { POSES } from './Olivia';

const SPEED_MS_PER_PERCENT = 40;
const MIN_ANIMATION_DURATION_MS = 800;
const ANIMATION_START_DELAY_MS = 500;

const PROGRESS_MESSAGES = {
  startNoGoal: 'Hi!\nLet\'s get started!',
  start: 'Complete tasks in the workshop to work towards your goal!',
  progress: 'Keep making progress in the workshop to hit your goal!',
  finish: '<b>Nice Job!</b>\nYou hit your usage goal for the week!',
};

const renderMessageList = (texts) => {
  const messages = texts
    .map((text, index) => `<li data-testid="login-modal-progress-message-${index + 1}">${parseMarkdownBold(text)}</li>`)
    .join('');
  return `<ul class="quest-messages">${messages}</ul>`;
};

const WorkshopWeeklyUsage = ({ className, questMessages }) => {
  const dispatch = useDispatch();

  const { weeklyUsageTime, lastShownUsageTime, celebrated: targetReached } = useSelector(getSelectedProductUsage);
  const hasUserInteracted = useSelector(getHasUserInteracted);
  const usageTarget = useSelector(getWeeklyUsageTarget);

  const [[startValue, endValue], setInitialValues] = useState([lastShownUsageTime || 0, weeklyUsageTime || 0]);
  const [celebrated, setCelebrated] = useState(!!targetReached);
  const [displayValue, setDisplayValue] = useState(startValue);
  const [showConfetti, setShowConfetti] = useState(false);
  const [isMessageVisible, setIsMessageVisible] = useState(false);

  const hasTarget = !!usageTarget;
  const startPercent = getClampedPercentage(startValue, usageTarget);
  const devButtonsEnabled = Env.isDevEnv && getUsageDevButtonsEnabled();
  const shouldCelebrate = hasTarget && startValue < usageTarget && endValue >= usageTarget;

  const counterRef = useRef({ value: startValue });
  const barRef = useRef(null);
  const characterRef = useRef(null);
  const oliviaRef = useRef(null);
  const timeoutsRef = useRef([]);
  const isAnimatingRef = useRef(false);
  const tlRef = useRef(null);

  const { playSfxFromMap, audioMap } = useWorkshopSfx();

  const setInitialCharacterState = (ref, { endValue: currentProgress, celebrated: isCelebrated } = {}) => {
    if (!ref) {
      return;
    }
    if (!hasTarget || currentProgress === 0) {
      ref.setPose(POSES.WAVE);
      return;
    }
    if (isCelebrated) {
      ref.setPose(POSES.DONE, { flip: 'left' });
      return;
    }
    ref.setPose(POSES.WALK);
  };

  const animateDisplayValue = (start, end, duration) => {
    gsap.killTweensOf(counterRef.current);
    counterRef.current.value = start;
    gsap.to(counterRef.current, {
      value: end,
      duration: duration / 1000,
      ease: 'none',
      onUpdate: () => setDisplayValue(Math.round(counterRef.current.value)),
    });
  };

  const celebrate = () =>
    new Promise((resolve) => {
      setCelebrated(true);
      setShowConfetti(true);
      setIsMessageVisible(true);
      const timeoutId = setTimeout(() => {
        oliviaRef.current?.celebrate({
          onComplete: () => {
            oliviaRef.current?.setPose(POSES.DONE);
            resolve();
          },
        });
      }, ANIMATION_START_DELAY_MS);
      timeoutsRef.current.push(timeoutId);
      if (hasUserInteracted) {
        playSfxFromMap(audioMap.weekly_usage_celebration);
      }
    });

  const animateProgress = () => {
    if (isAnimatingRef.current) return;

    isAnimatingRef.current = true;
    tlRef.current?.kill();
    tlRef.current = gsap.timeline();

    // Without a target the bar showed no progress, so usage is deliberately not persisted: the watermark
    // stays where it was and the student sees this week's progress animate - and celebrate - as usual
    // once the target is available again.
    if (!hasTarget) {
      tlRef.current
        .call(() => {
          oliviaRef.current?.play();
          setIsMessageVisible(true);
          isAnimatingRef.current = false;
        });

      return;
    }

    const endPercent = getClampedPercentage(endValue, usageTarget);
    const progressDelta = endPercent - startPercent;
    const duration =
      progressDelta === 0 ? 0 : Math.max(MIN_ANIMATION_DURATION_MS, Math.round(progressDelta * SPEED_MS_PER_PERCENT));
    const barDuration = Math.max(0, duration - 300);

    // Initialize inline styles as percentages so GSAP doesn't tween from a computed pixel value,
    // which causes a micro-movement when startPercent === endPercent (e.g. both capped at 100%).
    gsap.set(barRef.current, { width: `${startPercent}%` });
    gsap.set(characterRef.current, { left: `${startPercent}%` });

    tlRef.current
      .call(() => {
        if (endValue === 0 || progressDelta > 0) {
          oliviaRef.current?.play();
        }
        if (endValue === 0 || celebrated) {
          setIsMessageVisible(true);
        }
        // Display value animation, Phase 1: counter tracks bar fill, capped at usageTarget.
        // Skipped when bar doesn't move (progressDelta <= 0); phase 2 handles it instead.
        if (progressDelta > 0) {
          animateDisplayValue(Math.min(startValue, usageTarget), Math.min(endValue, usageTarget), barDuration);
        }
      })
      .to(barRef.current, {
        width: `${endPercent}%`,
        duration: barDuration / 1000,
        ease: 'none',
        onComplete: () => {
          // Display value animation, Phase 2: counter continues from usageTarget to endValue after bar is full.
          // When bar didn't move (progressDelta <= 0), animates the full range instead.
          if (endValue > usageTarget) {
            const phase2Start = progressDelta > 0 ? usageTarget : startValue;
            const phase2Duration = progressDelta > 0 ? 300 : MIN_ANIMATION_DURATION_MS;
            animateDisplayValue(phase2Start, endValue, phase2Duration);
          }
        },
      })
      .to(characterRef.current, { left: `${endPercent}%`, duration: duration / 1000, ease: 'none' }, '<')
      .call(async () => {
        if (shouldCelebrate || (endValue !== 0 && !celebrated)) {
          oliviaRef.current?.setPose(POSES.WALK);
        }

        if (!devButtonsEnabled) {
          dispatch(saveUsageData({ lastShownUsageTime: endValue }));
        }

        if (shouldCelebrate) {
          await celebrate();
        }

        setIsMessageVisible(true);
        isAnimatingRef.current = false;
      });
  };

  const mockUsageValues = (newValues) => {
    if (isAnimatingRef.current || !newValues?.length) {
      return;
    }

    const [start, end] = newValues;
    const newCelebrated = hasTarget && start >= usageTarget;

    tlRef.current?.kill();
    gsap.killTweensOf(counterRef.current);
    gsap.set(barRef.current, { clearProps: 'width' });
    gsap.set(characterRef.current, { clearProps: 'left' });
    setInitialCharacterState(oliviaRef.current, { endValue: end, celebrated: newCelebrated });

    setCelebrated(newCelebrated);
    setIsMessageVisible(false);
    setShowConfetti(false);
    setDisplayValue(start);
    setInitialValues(newValues);

    isAnimatingRef.current = false;
  };

  const currentMessage = useMemo(() => {
    const usageText = `You practiced for <b>${Number(endValue)} min</b> this week!`;

    if (shouldCelebrate) {
      return PROGRESS_MESSAGES.finish;
    }

    if (questMessages?.length) {
      const texts = questMessages.map(({ text }) => text);
      return renderMessageList(hasTarget || endValue === 0 ? texts : [usageText, ...texts]);
    }

    if (!hasTarget) {
      return endValue === 0 ? PROGRESS_MESSAGES.startNoGoal : usageText;
    }

    if (endValue === 0) {
      return PROGRESS_MESSAGES.start;
    }

    return endValue >= usageTarget ? PROGRESS_MESSAGES.finish : PROGRESS_MESSAGES.progress;
  }, [shouldCelebrate, hasTarget, usageTarget, endValue, questMessages]);

  useEffect(() => {
    const timeouts = timeoutsRef.current;
    const counter = counterRef.current;

    setInitialCharacterState(oliviaRef.current, { endValue, celebrated });

    timeouts.push(setTimeout(animateProgress, ANIMATION_START_DELAY_MS));

    return () => {
      timeouts.forEach((id) => clearTimeout(id));
      timeouts.length = 0;

      tlRef.current?.kill();
      gsap.killTweensOf(counter);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <ConfettiBurst active={showConfetti} />
      <UsageContainer className={className} data-dd-action-name="weekly-usage-content">
        <MessageContainer aria-live="polite" aria-atomic="true">
          {isMessageVisible && <Message dangerouslySetInnerHTML={{ __html: currentMessage }} />}
        </MessageContainer>

        <BarSection>
          <CharacterTrack $width={hasTarget && startValue >= usageTarget ? '80%' : '75%'}>
            <CharacterWrapper ref={characterRef} $leftPercent={startPercent}>
              <Olivia ref={oliviaRef} currentProgress={displayValue} goal={usageTarget} />
            </CharacterWrapper>
          </CharacterTrack>

          {hasTarget ? (
            <>
              <ProgressTrack
                role="progressbar"
                aria-label="Weekly usage progress"
                aria-valuenow={displayValue}
                aria-valuemin={0}
                aria-valuemax={usageTarget}
                aria-valuetext={`Progress: ${endValue} of ${usageTarget} minutes.`}
              >
                <ProgressFill ref={barRef} $widthPercent={startPercent} $showGradient={displayValue > usageTarget}>
                  {displayValue < usageTarget && <ProgressFromLabel>{displayValue}</ProgressFromLabel>}
                </ProgressFill>
                <ConfettiHorn
                  src={ConfettiHornSvg}
                  alt=""
                  aria-hidden="true"
                  $celebrate={showConfetti}
                  $hidden={celebrated && !showConfetti}
                />
                <ProgressToLabel style={{ color: displayValue >= usageTarget && 'white' }}>
                  {displayValue >= usageTarget ? displayValue : usageTarget} min
                </ProgressToLabel>
              </ProgressTrack>
              <MessageContainer aria-live="polite" aria-atomic="true">
                <span className="sr-only">
                  Progress: {endValue} of {usageTarget} minutes.
                </span>
              </MessageContainer>
            </>
          ) : (
            <ProgressTrack />
          )}
        </BarSection>

        {devButtonsEnabled && (
          <StyledDevButtons
            startValue={startValue}
            endValue={endValue}
            usageTarget={usageTarget}
            onSetButtonClick={mockUsageValues}
            onRunButtonClick={animateProgress}
          />
        )}
      </UsageContainer>
    </>
  );
};

WorkshopWeeklyUsage.propTypes = {
  className: PropTypes.string,
  questMessages: PropTypes.arrayOf(PropTypes.shape({ text: PropTypes.string })),
};

WorkshopWeeklyUsage.defaultProps = {
  className: '',
  questMessages: null,
};

export default WorkshopWeeklyUsage;

const bounce = keyframes`
  0% { transform: translateY(-100%) scale(0.9); }
  50% { transform: translateY(-100%) scale(0.7); }
  100% { transform: translateY(-100%) scale(0.9); }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const UsageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
`;

const MessageContainer = styled.div`
  width: 100%;
`;

const Message = styled.div`
  width: 100%;
  color: #413510;
  font-family: Open Sans;
  font-size: 21px;
  text-align: center;
  white-space: pre-line;
  opacity: 0;
  animation: ${fadeIn} 0.3s ease-in-out forwards;

  b {
    color: #413101;
    font-family: Quicksand Bold;
  }

  .quest-messages {
    text-align: left;
    font-size: 16px;
    margin: 0;
    padding-left: 20px;

    li {
      margin: 0;
    }
  }
`;

const CharacterTrack = styled.div`
  width: ${({ $width }) => $width || '75%'};
  position: relative;
  bottom: 0;
`;

const CharacterWrapper = styled.div`
  position: absolute;
  bottom: 7px;
  left: ${({ $leftPercent }) => $leftPercent}%;
`;

const BarSection = styled.div`
  width: 100%;
  position: relative;
  margin-top: auto;
`;

const ProgressTrack = styled.div`
  width: 100%;
  height: 40px;
  display: flex;
  border-radius: 20px;
  background: #e6f3f0;
  position: relative;
  font-family: Quicksand Bold;
  color: #464646;
  font-size: 21px;
  line-height: 35px;
  white-space: nowrap;
`;

const ProgressFill = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  width: ${({ $widthPercent }) => $widthPercent}%;
  padding: 0 10px;
  height: 100%;
  background: ${({ $showGradient }) => ($showGradient ? 'linear-gradient(to right, #499689 55%, #CD9864)' : '#499689')};
  border-radius: 20px;
  background-clip: padding-box;
  border: 4px solid transparent;
`;

const ProgressFromLabel = styled.span`
  color: #f6f6f6;
`;

const ProgressToLabel = styled.span`
  display: flex;
  flex-direction: column;
  align-items: center;
  transform: translateY(-50%);
  position: absolute;
  right: 15px;
  top: 50%;
`;

const ConfettiHorn = styled.img`
  width: 110px;
  height: 110px;
  position: absolute;
  transform: translateY(-100%);
  right: -5px;
  top: 0;
  transform-origin: 100% 100%;
  animation: ${({ $celebrate }) =>
    ($celebrate
      ? css`
          ${bounce} 0.4s cubic-bezier(0.1, -0.6, 0.2, 0), ${fadeOut} 0.3s ease-out 0.3s forwards
        `
      : 'none')};
  opacity: ${({ $hidden }) => ($hidden ? 0 : 1)};
`;

const StyledDevButtons = styled(DevButtons)`
  top: -40px;
`;
